/**
 * CS-1369 — Lightspeed (Vend) plan renewal date.
 *
 * Lightspeed's X-series API does not expose the plan renewal date, so it is captured manually
 * as a day of the month on the POS setup dialog. Everything derived from it — which dates are
 * still billable on cancellation and how long the retailer keeps access — is calculated here.
 *
 * Confirmed business rule: the account is cancelled 30 days after the next billing date; more
 * precisely, access runs up to the day *before* the following billing date.
 *
 * These are deliberately pure functions (no Angular, no locale services) so the month-clamping,
 * leap-year and year-rollover arithmetic can be unit tested directly.
 */

export const MIN_BILLING_DAY_OF_MONTH = 1;
export const MAX_BILLING_DAY_OF_MONTH = 31;

/** Month abbreviations used for the "25 Aug 2026" display format. */
const MONTH_ABBREVIATIONS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/** A blank billing day is legitimate — the date may not be known at initial setup. */
export function isBillingDayOfMonthEmpty(value: unknown): boolean {
  return value === null || value === undefined || `${value}`.trim().length === 0;
}

/** A non-empty billing day must be a whole day of the month: 1..31. */
export function isValidBillingDayOfMonth(value: unknown): boolean {
  if (isBillingDayOfMonthEmpty(value)) {
    return false;
  }
  const parsed = Number(value);
  return Number.isInteger(parsed)
    && parsed >= MIN_BILLING_DAY_OF_MONTH
    && parsed <= MAX_BILLING_DAY_OF_MONTH;
}

/** Optional field: empty passes, anything non-empty must be a valid day of the month. */
export function isAcceptableBillingDayOfMonth(value: unknown): boolean {
  return isBillingDayOfMonthEmpty(value) || isValidBillingDayOfMonth(value);
}

/** Number of days in the given month. `monthIndex` may overflow (12 = January of year + 1). */
export function lastDayOfMonth(year: number, monthIndex: number): number {
  return new Date(year, monthIndex + 1, 0).getDate();
}

/**
 * The billing date for `day` in the given month, clamped to the last day of that month when the
 * day does not exist there (billing day 31 in February 2027 becomes 28 Feb 2027).
 * `monthIndex` may overflow or underflow — the year rolls over accordingly.
 */
export function billingDateFor(year: number, monthIndex: number, day: number): Date {
  return new Date(year, monthIndex, Math.min(day, lastDayOfMonth(year, monthIndex)));
}

function atMidnight(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** The first billing date falling on or after `from`. */
export function nextBillingDate(from: Date, day: number): Date {
  const thisMonth = billingDateFor(from.getFullYear(), from.getMonth(), day);
  if (thisMonth.getTime() >= atMidnight(from).getTime()) {
    return thisMonth;
  }
  return billingDateFor(from.getFullYear(), from.getMonth() + 1, day);
}

export interface PosCancellationSchedule {
  /** The dates the account will still be billed on. */
  billingDates: Date[];
  /** The last day the retailer retains access — the day before the following billing date. */
  accessUntil: Date;
}

/**
 * Works out what a cancellation started at `from` will still cost.
 *
 * `monthsAhead` lets the user push the cancellation further out on the same day of the month;
 * every intervening month is added to the billable list.
 */
export function buildCancellationSchedule(
  billingDay: number,
  from: Date,
  monthsAhead: number = 0,
): PosCancellationSchedule {
  const first = nextBillingDate(from, billingDay);
  const billingDates: Date[] = [];
  for (let offset = 0; offset <= Math.max(0, monthsAhead); offset++) {
    billingDates.push(billingDateFor(first.getFullYear(), first.getMonth() + offset, billingDay));
  }

  const last = billingDates[billingDates.length - 1];
  const following = billingDateFor(last.getFullYear(), last.getMonth() + 1, billingDay);
  const accessUntil = new Date(following.getFullYear(), following.getMonth(), following.getDate() - 1);

  return {billingDates, accessUntil};
}

/**
 * The dates offered in the cancellation dropdown.
 *
 * These are *access-until* dates, not billing dates: the operator picks the last day the retailer
 * keeps the account, and the billable dates follow from that choice. Index n corresponds to
 * `monthsAhead = n` in {@link buildCancellationSchedule}.
 */
export function accessUntilOptions(billingDay: number, from: Date, count: number = 12): Date[] {
  return Array.from({length: count}, (_unused, monthsAhead) =>
    buildCancellationSchedule(billingDay, from, monthsAhead).accessUntil);
}

/** "25 Aug 2026" — deliberately locale-independent so the copy is identical everywhere. */
export function formatBillingDate(date: Date): string {
  return `${date.getDate()} ${MONTH_ABBREVIATIONS[date.getMonth()]} ${date.getFullYear()}`;
}

/**
 * "Aug 2026" — the billing *month* without the day.
 *
 * The captured value is Lightspeed's plan renewal day, which is not the same as the day we bill
 * the client, so naming an exact date would state something we cannot actually promise. The month
 * is enough for the operator to see what is still owed; the access-until date stays exact because
 * that is the part the retailer is held to.
 */
export function formatBillingMonth(date: Date): string {
  return `${MONTH_ABBREVIATIONS[date.getMonth()]} ${date.getFullYear()}`;
}

export function formatBillingMonths(dates: Date[]): string {
  return dates.map(formatBillingMonth).join(', ');
}

// --- Copy (CS-1369 decision 1 — verbatim, do not reword) ---------------------------------------

export const BILLING_DATE_MISSING_MESSAGE =
  'Lightspeed plan renewal date is missing. Add it in POS setup so cancellation and billing dates can be calculated.';

export const BILLING_DATE_MISSING_BUTTON_LABEL = 'POS setup';

export const NO_BILLING_DATE_CANCELLATION_MESSAGE =
  'No plan renewal date has been captured for this Lightspeed account, so billing and access dates cannot be calculated. Add the date in POS setup, or continue and select a cancellation date manually.';

export const CANCELLATION_EXPLANATION_MESSAGE =
  'The account is still billed for the month(s) shown below. The retailer keeps access until the date selected, after which the account is closed.';

export const STILL_TO_BE_BILLED_LABEL = 'Still to be billed:';

export const ACCESS_UNTIL_LABEL = 'Access until:';

